import { Copy01, Pin01, Pin02 as PinOff, Share03, Trash01 } from '@timelyapp/icons/solid-icons';
import type { DropdownMenuItem } from '@timelyapp/react';
import classNames from 'classnames';
import Avatar from 'components/Avatar';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import { WidgetBarChartIcon } from 'components/superreports/WidgetIcons';
import Tooltip from 'components/Tooltip';
import type { ReportListItem } from 'factories/superreports';
import { useWithDispatch } from 'hooks/useWithDispatch';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { togglePinOnReportId } from 'redux/modules/pins.actions';
import {
  deleteReportId,
  duplicateReportIdToDraft,
  shareReportId,
} from 'redux/modules/superreports.actions';
import type { UserPermissionsFacade } from 'redux/modules/user.types';
import { Card } from './Card';
import styles from './index.module.scss';

export interface ReportCardItemProps {
  report: ReportListItem;
  className?: string;
  onClick: (report: ReportListItem) => void;
  permissions: UserPermissionsFacade;
  features?: any;
}

const MAX_VISIBLE_NAME_LENGTH = 25;

const ReportCard = React.memo<ReportCardItemProps>(props => {
  const { className, report, permissions, onClick } = props;
  const containerClass = classNames(styles.reportCard, className);
  const navigate = useNavigate();

  // Setup Redux actions
  const actions = useWithDispatch({
    togglePinOnReportId,
    duplicateReportIdToDraft,
    deleteReportId,
    shareReportId,
  });

  // Handle click event
  const handleClick = useCallback(() => {
    onClick(report);
  }, [onClick, report]);

  // Create dropdown items specifically for reports
  const dropdownItems = useMemo<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [];

    // Pin/Unpin option
    if (report.isPinned) {
      items.push({
        type: 'item',
        key: 'unpin',
        label: 'Unpin template',
        icon: <PinOff />,
        onSelect: () => actions.togglePinOnReportId(report.id),
      });
    } else {
      items.push({
        type: 'item',
        key: 'pin',
        label: 'Pin template',
        icon: <Pin01 />,
        onSelect: () => actions.togglePinOnReportId(report.id),
      });
    }

    // Duplicate option
    if (permissions.canCreate.reports) {
      items.push({
        type: 'item',
        key: 'duplicate',
        label: 'Duplicate',
        icon: <Copy01 />,
        onSelect: async () => {
          await actions.duplicateReportIdToDraft(report.id);
          navigate('/superreports/new');
        },
      });

      // Share option
      items.push({
        type: 'item',
        key: 'share',
        label: 'Share snapshot report',
        icon: <Share03 />,
        onSelect: () => actions.shareReportId(report.id),
      });
    }

    items.push({ type: 'separator' });

    // Delete option with confirmation
    if (permissions.canDelete.reports) {
      items.push({
        type: 'item',
        key: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: <Trash01 />,
        confirm: {
          prompt: `Are you sure you want to delete "${report.name}"? This action cannot be undone.`,
          confirmButtonDestructive: true,
          confirmLabel: 'Yes, delete',
        },
        onSelect: () => actions.deleteReportId(report.id),
      });
    }

    return items;
  }, [
    report.id,
    report.name,
    report.isPinned,
    actions,
    permissions.canCreate.reports,
    permissions.canDelete.reports,
    navigate,
  ]);

  return (
    <Card
      className={containerClass}
      clickReference={report}
      onClick={handleClick}
      dropdownItems={dropdownItems}
    >
      {report.isPinned && (
        <div className={styles.pinHideOnHover}>
          <Pin01 />
        </div>
      )}
      <div className={styles.title}>
        <WidgetBarChartIcon />
        <h3 data-testid="report-name-report-card">
          <DiagnosticResourceId resource="report" id={report.id} />
          {/* CSS auto truncates when longer than this, so add a tooltip */}
          <Tooltip title={report.name.length > MAX_VISIBLE_NAME_LENGTH ? report.name : undefined}>
            <span className={styles.titleText}>{report.name}</span>
          </Tooltip>
        </h3>
      </div>
      <div className={styles.date}>{report.updatedAt}</div>
      <div className={styles.createdBy}>
        <Avatar
          size="medium"
          name={report.createdBy.name}
          src={report.createdBy.avatar.medium_retina}
          fallbackToInitials
          shape="circle"
        />
        <span>{report.createdBy.name}</span>
      </div>
      <div className={styles.description}>
        <p data-testid="report-desc-report-card">{report.description}</p>
      </div>
    </Card>
  );
});

ReportCard.displayName = 'ReportCard';
export { ReportCard };
